/**
 * DevicesSidebar — árvore navegável Site → Equipment usada no split panel
 * da tela Admin → Devices.
 *
 * Cada nó mostra contagem de devices ativos e uma bolinha de saúde agregada
 * (verde/amarelo/vermelho) baseada nos device_statuses recebidos.
 *
 * Estado de expansão dos sites persiste em localStorage. Nó selecionado é
 * gerenciado pelo pai (via prop) pra suportar sync com query string.
 */

import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { AlertTriangle, Building2, ChevronDown, ChevronRight, Package, Snowflake } from "lucide-react";

import type { Device, DeviceStatus, Equipment, Site } from "@/types";

export type SelectedNode =
  | { kind: "all" }
  | { kind: "site"; id: string }
  | { kind: "equipment"; id: string }
  | { kind: "orphans" };

interface Props {
  sites: Site[];
  equipments: Equipment[];
  devices: Device[];
  statusByDevice: Map<string, DeviceStatus>;
  orphansCount: number;
  selected: SelectedNode;
  onSelect: (node: SelectedNode) => void;
}

/** Bolinha de saúde agregada — pior do conjunto vence. */
function healthDot(devices: Device[], statusByDevice: Map<string, DeviceStatus>) {
  if (devices.length === 0) return "empty";
  let worst: "ok" | "stale" | "offline" = "ok";
  for (const d of devices) {
    const s = statusByDevice.get(d.id);
    if (!s) continue;
    if (!s.online) return "offline";
    if (s.is_stale && worst === "ok") worst = "stale";
  }
  return worst;
}

const DOT_CLASS: Record<string, string> = {
  ok:      "bg-emerald-500",
  stale:   "bg-amber-400",
  offline: "bg-red-500",
  empty:   "bg-slate-300 dark:bg-slate-600",
};

const EXPANDED_KEY = "envpulse:devices-sidebar-expanded";

function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveExpanded(ids: Set<string>) {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore quota */
  }
}

export function DevicesSidebar({
  sites, equipments, devices, statusByDevice,
  orphansCount, selected, onSelect,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => loadExpanded());

  useEffect(() => saveExpanded(expanded), [expanded]);

  // Indexa equipments/devices por site pra evitar filters aninhados no render
  const equipmentsBySite = useMemo(() => {
    const m = new Map<string, Equipment[]>();
    for (const e of equipments) {
      const arr = m.get(e.site_id) ?? [];
      arr.push(e);
      m.set(e.site_id, arr);
    }
    return m;
  }, [equipments]);

  const devicesByEquipment = useMemo(() => {
    const m = new Map<string, Device[]>();
    for (const d of devices) {
      if (!d.equipment_id) continue;
      const arr = m.get(d.equipment_id) ?? [];
      arr.push(d);
      m.set(d.equipment_id, arr);
    }
    return m;
  }, [devices]);

  const devicesBySite = useMemo(() => {
    const m = new Map<string, Device[]>();
    for (const d of devices) {
      const arr = m.get(d.site_id) ?? [];
      arr.push(d);
      m.set(d.site_id, arr);
    }
    return m;
  }, [devices]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const isSelectedAll = selected.kind === "all";
  const isSelectedOrphans = selected.kind === "orphans";

  return (
    <nav className="space-y-1 text-sm" aria-label="Sites e ativos">
      {/* Nó raiz "Todos" */}
      <NodeButton
        indent={0}
        label="Todos os devices"
        icon={<Package className="h-4 w-4" />}
        count={devices.length}
        active={isSelectedAll}
        onClick={() => onSelect({ kind: "all" })}
      />

      {/* Sites */}
      {sites.map((site) => {
        const isExpanded = expanded.has(site.id);
        const siteEquipments = equipmentsBySite.get(site.id) ?? [];
        const siteDevices = devicesBySite.get(site.id) ?? [];
        const siteHealth = healthDot(siteDevices, statusByDevice);
        const siteActive = selected.kind === "site" && selected.id === site.id;

        return (
          <div key={site.id}>
            <NodeButton
              indent={0}
              label={site.name}
              icon={<Building2 className="h-4 w-4 text-brand-600 dark:text-brand-400" />}
              count={siteDevices.length}
              active={siteActive}
              onClick={() => onSelect({ kind: "site", id: site.id })}
              onToggle={siteEquipments.length ? () => toggle(site.id) : undefined}
              expanded={isExpanded}
              healthTone={siteHealth}
            />

            {isExpanded && (
              <div>
                {siteEquipments.map((eq) => {
                  const eqDevices = devicesByEquipment.get(eq.id) ?? [];
                  const eqHealth = healthDot(eqDevices, statusByDevice);
                  const eqActive = selected.kind === "equipment" && selected.id === eq.id;
                  return (
                    <NodeButton
                      key={eq.id}
                      indent={1}
                      label={eq.name}
                      icon={<Snowflake className="h-4 w-4 text-slate-500 dark:text-slate-400" />}
                      count={eqDevices.length}
                      active={eqActive}
                      onClick={() => onSelect({ kind: "equipment", id: eq.id })}
                      healthTone={eqHealth}
                      dimmed={!eq.active}
                    />
                  );
                })}
                {siteEquipments.length === 0 && (
                  <div className="pl-9 py-1 text-xs italic text-slate-400 dark:text-slate-600">
                    sem ativos
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Órfãos */}
      {orphansCount > 0 && (
        <>
          <div className="mt-3 mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Não vinculados
          </div>
          <NodeButton
            indent={0}
            label="Órfãos"
            icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
            count={orphansCount}
            active={isSelectedOrphans}
            onClick={() => onSelect({ kind: "orphans" })}
          />
        </>
      )}
    </nav>
  );
}

// ---------------------------------------------------------------------------

function NodeButton({
  indent, label, icon, count, active, onClick, onToggle, expanded, healthTone, dimmed,
}: {
  indent: 0 | 1;
  label: string;
  icon: React.ReactNode;
  count: number;
  active: boolean;
  onClick: () => void;
  onToggle?: () => void;
  expanded?: boolean;
  healthTone?: string;
  dimmed?: boolean;
}) {
  return (
    <div
      className={clsx(
        "group flex items-center rounded-md",
        active
          ? "bg-brand-100/70 dark:bg-brand-900/40"
          : "hover:bg-surface-100/70 dark:hover:bg-slate-800/60",
        indent === 1 && "ml-4",
      )}
    >
      {onToggle ? (
        <button
          type="button"
          className="flex-none px-1 py-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          onClick={onToggle}
          aria-label={expanded ? "Recolher" : "Expandir"}
        >
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <span className="w-6" />
      )}
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          "flex flex-1 items-center gap-2 py-1.5 pr-2 text-left",
          active
            ? "font-semibold text-brand-900 dark:text-brand-100"
            : "text-slate-700 dark:text-slate-300",
          dimmed && "opacity-50",
        )}
      >
        {icon}
        <span className="flex-1 truncate">{label}</span>
        {healthTone && (
          <span
            className={clsx("h-2 w-2 rounded-full", DOT_CLASS[healthTone])}
            title={healthTone}
          />
        )}
        <span className="tabular-nums text-[11px] text-slate-500 dark:text-slate-400">
          {count}
        </span>
      </button>
    </div>
  );
}
